import { IconTile } from "@design";
import { ChevronRight } from "lucide-react";
import { cloneElement, type ReactElement, type ReactNode } from "react";
import { Link } from "react-router";
import { Text } from "@/components/Text";

/** One linked row in a help list: optional icon tile, title, a line of detail. The whole row is the link. */
export function HelpRow({
  to,
  title,
  detail,
  icon,
  brand = false,
}: Readonly<{
  to: string;
  title: ReactNode;
  detail?: ReactNode;
  icon?: ReactElement<{ size?: number }>;
  brand?: boolean;
}>) {
  return (
    <li>
      <Link
        to={to}
        className="flex items-center gap-3 px-4 py-3 transition-colors btn-focus-ring hover:bg-surface-secondary"
      >
        {icon && <IconTile variant={brand ? "brand" : "neutral"}>{cloneElement(icon, { size: 16 })}</IconTile>}
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <Text as="span" weight="semibold">
            {title}
          </Text>
          {detail && (
            <Text as="span" variant="body-sm" tone="secondary">
              {detail}
            </Text>
          )}
        </span>
        <ChevronRight size={16} aria-hidden="true" className="shrink-0 text-content-tertiary" />
      </Link>
    </li>
  );
}

/** A titled, divided list of HelpRows. */
export function HelpList({ title, hint, children }: Readonly<{ title: string; hint?: string; children: ReactNode }>) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <Text variant="h3" as="h2">
          {title}
        </Text>
        {hint && (
          <Text variant="body-sm" tone="secondary">
            {hint}
          </Text>
        )}
      </div>
      <ul className="divide-y divide-border-subtle overflow-hidden rounded-xl border border-border-subtle bg-surface-primary">
        {children}
      </ul>
    </section>
  );
}
